import React, { useCallback, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

/** The modal box: a column whose header stays put while the body below it scrolls (spread into the Paper's sx). */
export const modalScrollPaper = { display: 'flex', flexDirection: 'column', overflow: 'hidden' } as const;
/** The modal's scrolling body (spread into its sx, and give it the onScroll from useScrolledDown). */
export const modalScrollBody = { flex: 1, minHeight: 0, overflowY: 'auto' } as const;

/** Whether a scrolling box is scrolled down, for a header that shrinks once the content moves under it. Compacts past
 * 40px and expands again only near the top, so it can't flicker at the boundary. */
export const useScrolledDown = (): [boolean, (e: React.UIEvent<HTMLElement>) => void] => {
  const [down, setDown] = useState(false);
  const onScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
    const y = e.currentTarget.scrollTop;
    setDown((was) => (was ? y > 8 : y > 40));
  }, []);
  return [down, onScroll];
};

const EASE = '280ms cubic-bezier(0.4, 0, 0.2, 1)';

/** Animates a row between its content's height and nothing (grid rows transition smoothly, unlike height: auto). */
const Fold: React.FC<{ open: boolean; children: React.ReactNode }> = ({ open, children }) => (
  <Box
    aria-hidden={!open}
    sx={{
      display: 'grid',
      gridTemplateRows: open ? '1fr' : '0fr',
      opacity: open ? 1 : 0,
      transition: `grid-template-rows ${EASE}, opacity ${EASE}`,
    }}
  >
    <Box sx={{ overflow: 'hidden', minHeight: 0 }}>{children}</Box>
  </Box>
);

/** A modal's title and close button, above its scrolling body. While the body is scrolled, the full title folds away
 * and a one-line version fades in, so the header shrinks smoothly to a single line. */
export const ModalHeader: React.FC<{ title: string; onClose: () => void; compact: boolean }> = ({
  title,
  onClose,
  compact,
}) => (
  <Box
    sx={{
      flexShrink: 0,
      px: 3,
      pt: compact ? 1.25 : 3,
      pb: compact ? 1.25 : 2,
      borderBottom: 1,
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      transition: `padding ${EASE}`,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Fold open={!compact}>
        <Typography variant="h5" component="h2" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
          {title}
        </Typography>
      </Fold>
      <Fold open={compact}>
        <Typography
          title={title}
          sx={{ fontSize: '1rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {title}
        </Typography>
      </Fold>
    </Box>
    <IconButton
      onClick={onClose}
      aria-label="Close"
      sx={{ alignSelf: compact ? 'center' : 'flex-start', mt: compact ? 0 : -0.5, mr: -1 }}
    >
      <Close />
    </IconButton>
  </Box>
);
